import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import { Person, PersonClient, PersonCreateModel, Role, RoleClient, Gender } from '../../utils/apiClient';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';
import { DateLocal, DateType } from '../../common/components/DateLocal/DateLocal';

// PersonCreateModel не содержит email в сгенерированном клиенте — расширяем локально
interface PersonCreateModelWithEmail extends PersonCreateModel {
  email: string;
}

interface CreatePersonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPersonCreated: (person: Person) => void;
}

const emptyForm = (): PersonCreateModelWithEmail => Object.assign(
  new PersonCreateModel({ lastName: '', firstName: '', patronymic: '', birthDate: new Date(), city: '', roleIds: [], gender: Gender.Male }),
  { email: '' }
);

const CreatePersonModal: React.FC<CreatePersonModalProps> = ({ isOpen, onClose, onPersonCreated }) => {
  const appContext = React.useContext(AppContext);
  const [form, setForm] = useState<PersonCreateModelWithEmail>(emptyForm());
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createdPerson, setCreatedPerson] = useState<Person | null>(null);

  const personClient = new PersonClient(ApiUrl, axios);
  const roleClient = new RoleClient(ApiUrl, axios);

  useEffect(() => {
    if (!isOpen) return;
    setForm(emptyForm());
    setCreatedPerson(null);
    const fetchRoles = async () => {
      setLoading(true);
      try {
        const data = await roleClient.getRoles();
        setRoles(data.filter(r => r.name !== 'Админ'));
      } catch (ex) {
        handleError(appContext!.setNotificationData, ex);
      } finally {
        setLoading(false);
      }
    };
    fetchRoles();
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(Object.assign(new PersonCreateModel({ ...form }), { ...form, [name]: value }) as PersonCreateModelWithEmail);
  };


  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const roleId = parseInt(e.target.value);
    const ids = form.roleIds || [];
    const roleIds = e.target.checked ? [...ids, roleId] : ids.filter(id => id !== roleId);
    setForm(Object.assign(new PersonCreateModel({ ...form }), { ...form, roleIds }) as PersonCreateModelWithEmail);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = new PersonCreateModel({
        ...form,
        email: form.email?.trim() || undefined,
      });
      const person = await personClient.createPerson(payload);
      setCreatedPerson(person);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    } finally {
      setSaving(false);
    }
  };

  const handleRegisterOnTournament = () => {
    if (createdPerson) onPersonCreated(createdPerson);
  };

  const handleClose = () => {
    setForm(emptyForm());
    setCreatedPerson(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="admin-participant-modal-overlay" onClick={handleClose}>
      <div className="admin-participant-modal" onClick={e => e.stopPropagation()}>
        <div className="admin-participant-modal__header">
          <h2>Новый участник</h2>
          <button className="admin-participant-modal__close" onClick={handleClose}>×</button>
        </div>

        <div className="admin-participant-modal__content">
          {loading ? (
            <div className="admin-participant-modal__loading">Загрузка...</div>
          ) : createdPerson ? (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <div className="admin-participant-modal__success" style={{ marginBottom: '1.5rem' }}>
                Участник <strong>{createdPerson.lastName} {createdPerson.firstName}</strong> успешно создан!
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                <button className="btn" onClick={handleRegisterOnTournament}>
                  Зарегистрировать на турнир
                </button>
                <button className="btn btn--back" onClick={handleClose}>
                  Закрыть
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="admin-participant-modal__form">
              <div className="admin-participant-modal__form-row">
                <div className="admin-participant-modal__form-group">
                  <label>Фамилия <span className="required">*</span></label>
                  <input name="lastName" value={form.lastName} onChange={handleChange} required />
                </div>
                <div className="admin-participant-modal__form-group">
                  <label>Имя <span className="required">*</span></label>
                  <input name="firstName" value={form.firstName} onChange={handleChange} required />
                </div>
              </div>

              <div className="admin-participant-modal__form-row">
                <div className="admin-participant-modal__form-group">
                  <label>Отчество</label>
                  <input name="patronymic" value={form.patronymic || ''} onChange={handleChange} />
                </div>
                <div className="admin-participant-modal__form-group">
                  <label>Дата рождения <span className="required">*</span></label>
                  <DateLocal
                    type={DateType.date}
                    value={form.birthDate ? new Date(form.birthDate) : undefined}
                    onChange={(date) => date && setForm(Object.assign(new PersonCreateModel({ ...form }), { ...form, birthDate: date }) as PersonCreateModelWithEmail)}
                    width={200}
                    isClearable={false}
                  />
                </div>
              </div>

              <div className="admin-participant-modal__form-row">
                <div className="admin-participant-modal__form-group">
                  <label>Пол <span className="required">*</span></label>
                  <select
                    name="gender"
                    value={form.gender ?? Gender.Male}
                    onChange={handleChange}
                    required
                  >
                    <option value={Gender.Male}>Мальчик</option>
                    <option value={Gender.Female}>Девочка</option>
                  </select>
                </div>
                <div className="admin-participant-modal__form-group">
                  <label>Город <span className="required">*</span></label>
                  <input name="city" value={form.city} onChange={handleChange} required />
                </div>
              </div>

              <div className="admin-participant-modal__form-row">
                <div className="admin-participant-modal__form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email || ''}
                    onChange={handleChange}
                    placeholder="необязательно"
                  />
                </div>
              </div>

              <div className="admin-participant-modal__form-group" style={{ marginTop: '0.5rem' }}>
                <label>Роли</label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                  {roles.map(role => (
                    <label key={role.id} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        value={role.id}
                        checked={(form.roleIds || []).includes(role.id!)}
                        onChange={handleRoleChange}
                      />
                      {role.name}
                    </label>
                  ))}
                </div>
              </div>

              <div className="admin-participant-modal__actions" style={{ marginTop: '1.25rem' }}>
                <button type="button" className="admin-participant-modal__btn admin-participant-modal__btn--cancel" onClick={handleClose}>
                  Отмена
                </button>
                <button type="submit" className="admin-participant-modal__btn admin-participant-modal__btn--save" disabled={saving}>
                  {saving ? 'Сохранение...' : 'Создать участника'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatePersonModal;
